import * as React from "react";
import { useOfferMode } from "@nju/data-access-portal-offers";
import { IPaymentMethod } from "@nju/data-access-portal-user";
import {
  ChangePaymentButton,
  ChangePaymentPendingInfo,
  SwitchFtthPaymentModal,
} from "@nju/feature-portal-sidebar";
import { Icon } from "@nju/icons";
import { BlockText, Modal, Separator, Text, useOverlay } from "@nju/ui";
import { formatPrice } from "@nju/utils";

interface IPaymentsProps {
  bundleDiscount?: number;
  paymentMethod?: IPaymentMethod;
  installationPrice: number | undefined;
  activationFee: number;
  subscriptionPrice: number;
  sfhPrice: number | undefined;
  isPending: boolean;
  userId: string;
}

export function Payments({
  sfhPrice,
  isPending,
  paymentMethod,
  bundleDiscount,
  installationPrice,
  activationFee,
  subscriptionPrice,
  userId,
}: IPaymentsProps) {
  const isDigitalWallet = paymentMethod?.methodType === "DIGITAL_WALLET";
  const isCreditCard = paymentMethod?.methodType === "CREDIT_CARD";
  const modal = useOverlay();
  const mode = useOfferMode();

  const openModal = React.useCallback(() => {
    modal.open();
  }, [modal]);

  return (
    <>
      <div className="flex items-start basis-full from-tablet:basis-1/2">
        {isDigitalWallet && (
          <>
            <Icon
              className="mr-3 w-[24px] h-[24px] from-tablet:w-[32px] from-tablet:h-[32px]"
              name="wallet"
              tone="accent"
            />
            <div className="flex-1">
              <Text
                size={{
                  "@onlyMobile": "s",
                  "@fromTablet": "m",
                }}
              >
                wybrany sposób płatności
              </Text>
              <BlockText
                className="block"
                size={{
                  "@onlyMobile": "s",
                  "@fromTablet": "l",
                }}
                weight="bold"
              >
                BLIK lub płatność online
              </BlockText>
              {isPending && <ChangePaymentPendingInfo tone="neutral" />}
              <ChangePaymentButton
                isPending={isPending}
                mode={mode}
                openModal={openModal}
                tone="accent"
                userId={userId}
              />
            </div>
          </>
        )}
        {isCreditCard && (
          <>
            <Icon
              className="mr-3 w-[24px] h-[24px] from-tablet:w-[32px] from-tablet:h-[32px]"
              name="credit-card"
              tone="accent"
            />
            <div className="flex-1">
              <Text
                size={{
                  "@onlyMobile": "s",
                  "@fromTablet": "m",
                }}
              >
                wybrany sposób płatności
              </Text>
              <BlockText
                className="block"
                size={{
                  "@onlyMobile": "s",
                  "@fromTablet": "l",
                }}
                weight="bold"
              >
                karta płatnicza **** **** ****{" "}
                {paymentMethod?.details.maskedCardNumber}
              </BlockText>
              {isPending && <ChangePaymentPendingInfo tone="neutral" />}
              <ChangePaymentButton
                isPending={isPending}
                mode={mode}
                openModal={openModal}
                tone="accent"
                userId={userId}
              />
              <Separator size="xs" />
            </div>
          </>
        )}
      </div>
      <Separator size="m" />
      {/* necessary condition to present 0 price */}
      {(installationPrice || installationPrice === 0) && (
        <>
          <div className="flex items-center basis-full from-tablet:basis-1/2">
            <Icon
              className="mr-3 w-[24px] h-[24px] from-tablet:w-[32px] from-tablet:h-[32px]"
              name="user-hard-hat"
              tone="accent"
            />
            <div className="flex-1">
              <Text
                size={{
                  "@onlyMobile": "s",
                  "@fromTablet": "m",
                }}
              >
                jednorazowa opłata instalacyjna
              </Text>
              <BlockText
                className="block"
                size={{
                  "@onlyMobile": "s",
                  "@fromTablet": "l",
                }}
                weight="bold"
              >
                {`${formatPrice(installationPrice)} zł`}
              </BlockText>
            </div>
          </div>
          <Separator size="xs" tone="invisible" />
        </>
      )}

      <div className="flex items-center basis-full from-tablet:basis-1/2">
        <Icon
          className="mr-3 w-[24px] h-[24px] from-tablet:w-[32px] from-tablet:h-[32px]"
          name="wallet"
          tone="accent"
        />
        <div className="flex-1">
          <Text
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "m",
            }}
          >
            jednorazowa opłata aktywacyjna
          </Text>
          <BlockText
            className="block"
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "l",
            }}
            weight="bold"
          >
            {`${formatPrice(activationFee ?? 0)} zł`}
          </BlockText>
        </div>
      </div>
      <Separator size="xs" tone="invisible" />

      <div className="flex items-center basis-full from-tablet:basis-1/2">
        <Icon
          className="mr-3 w-[24px] h-[24px] from-tablet:w-[32px] from-tablet:h-[32px]"
          name="wallet"
          tone="accent"
        />
        <div className="flex-1">
          <Text
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "m",
            }}
          >
            miesięczny koszt usługi
          </Text>
          <BlockText
            className="block"
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "l",
            }}
            weight="bold"
          >
            {`${formatPrice(subscriptionPrice)} zł`}
          </BlockText>
        </div>
      </div>
      <Separator size="xs" tone="invisible" />
      {sfhPrice && (
        <div className="flex items-center basis-full from-tablet:basis-1/2">
          <Icon
            className="mr-3 w-[24px] h-[24px] from-tablet:w-[32px] from-tablet:h-[32px]"
            name="house"
            tone="accent"
          />
          <div className="flex-1">
            <Text
              size={{
                "@onlyMobile": "s",
                "@fromTablet": "m",
              }}
            >
              miesięczna dopłata za internet w budynkach jednorodzinnych
            </Text>
            <BlockText
              className="block"
              size={{
                "@onlyMobile": "s",
                "@fromTablet": "l",
              }}
              weight="bold"
            >
              {`${formatPrice(sfhPrice)} zł`}
            </BlockText>
          </div>
        </div>
      )}
      {bundleDiscount && (
        <div className="flex items-center mt-4 gap-4">
          <div className="w-6 h-6">
            <Icon height={32} name="badge-percent" tone="accent" width={32} />
          </div>
          <Text size={{ "@onlyMobile": "s", "@fromTablet": "m" }}>
            rabat {bundleDiscount} zł miesięcznie na nju subskrypcja, gdy masz
            aktywną tę usługę
          </Text>
        </div>
      )}
      {modal.isOpened && (
        <Modal onClose={modal.close}>
          <SwitchFtthPaymentModal
            closeModal={() => {
              return modal.close();
            }}
          />
        </Modal>
      )}
    </>
  );
}
